import { useRenderer } from '@fox/atoms/hooks/use-renderer';
import { Button } from '@fox/atoms/src/button/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { getContrastColor } from '@fox/atoms/utils/color-contrast';
import { NBSP } from '@fox/atoms/utils/string';
import { getElementsOfTypeNested } from '@fox/blocks/utils/get-elements';
import { useSubscribeTopicWithHandler } from '@fox/extensions/src/extension-hooks';
import { Inset } from '@fox/layout/src/flex';
import {
	PAYMENT_BLOCK_ANSWER_TYPE,
	SURVEY_BUTTON_NEXT_BLOCK_TYPE,
	SURVEY_BUTTON_PREVIOUS_BLOCK_TYPE,
	SURVEY_BUTTON_SUBMIT_BLOCK_TYPE,
	TICKET_BLOCK_TYPE
} from '../../../form-block-types';
import { useFormErrorContext } from '../../form-error-context/form-error-context';
import { EventCapacityStatus } from '../../form-event-context/contracts';
import { useFormEventContext } from '../../form-event-context/form-event-context';
import { FORM_QUESTION_ANSWERED_TOPIC, FormRenderMode, QuestionStyleTypes, SurveyState } from '../../form-runtime-context/contracts';
import { useFormRuntimeContext, useQuestionStyle } from '../../form-runtime-context/form-runtime-context';
import { RequiredAsterisk } from '../../required-asterisk/required-asterisk';
import { checkAllowNext, haveRequiredFields } from './util/allow-next';

const IGNORED_PAGE_ERRORS = [PAYMENT_BLOCK_ANSWER_TYPE, TICKET_BLOCK_TYPE];

export const FormPagination = () => {
	const {
		formRenderMode,
		getAnswers,
		currentPage: formCurrentPage,
		sections,
		layout,
		goToPreviousPage,
		goToNextPage,
		owner,
		submitForm,
		submitButtonDisabled,
		submitButtonLabel,
		surveyState
	} = useFormRuntimeContext();
	const elements: (JSX.Element | string)[] = [];
	const render = useRenderer();
	useSubscribeTopicWithHandler(FORM_QUESTION_ANSWERED_TOPIC, () => {
		render();
	});
	const { primaryColour } = useQuestionStyle(QuestionStyleTypes.general, {});
	const currentPage = sections[formCurrentPage ?? 0];
	const required = haveRequiredFields(currentPage?.fields);

	let currentPageLayout: any = //fix this later
		layout?.layouts?.[0]?.props?.innerLayout?.layouts?.find(l => {
			return l?.props?.columnContents?.find(c => {
				return c?.items?.find(i => {
					return i?.props?.branchId === currentPage?.branchId;
				});
			});
		}) ?? undefined;

	const hasCustomNextButton = currentPageLayout
		? getElementsOfTypeNested(currentPageLayout, SURVEY_BUTTON_NEXT_BLOCK_TYPE)[0] !== undefined
		: false;
	const hasCustomPreviousButton = currentPageLayout
		? getElementsOfTypeNested(currentPageLayout, SURVEY_BUTTON_PREVIOUS_BLOCK_TYPE)[0] !== undefined
		: false;
	const hasCustomSubmitButton = currentPageLayout
		? getElementsOfTypeNested(currentPageLayout, SURVEY_BUTTON_SUBMIT_BLOCK_TYPE)[0] !== undefined
		: false;

	// TODO - Update error context for an answer if it doesn't pass requirement validation in checkAllowNext, pass setErrors into it
	const formErrorContext = useFormErrorContext();
	// TODO this currentPageErrors doesn't play very nicely with other error/validation we have
	const currentPageErrors = currentPage?.fields?.some(f => !!formErrorContext?.errors[f.name] && !IGNORED_PAGE_ERRORS.includes(f.type));
	const eventContext = useFormEventContext();
	const onWaitList = eventContext?.event?.status === EventCapacityStatus.OnWaitlist;
	const allowNext = !currentPageErrors && checkAllowNext({ currentPage, getAnswers, layout, sections, formRenderMode, waitlistSubmit: onWaitList });
	const shouldRenderNext = goToNextPage && !hasCustomNextButton && surveyState !== SurveyState.submitted;
	const shouldRenderPrevious = goToPreviousPage && !hasCustomPreviousButton && surveyState !== SurveyState.submitted;
	const shouldRenderSubmit =
		!hasCustomSubmitButton &&
		owner?.ownerType !== 'Event' &&
		formRenderMode === FormRenderMode.runtime &&
		surveyState !== SurveyState.submitted &&
		!goToNextPage;

	if (shouldRenderPrevious) {
		elements.push(
			<Button
				style={
					primaryColour
						? {
								color: primaryColour,
								backgroundColor: getContrastColor(primaryColour)
							}
						: {}
				}
				variant="secondary"
				color="primary"
				key="form-action-back"
				onClick={goToPreviousPage}
				size="small"
			>
				Back
			</Button>
		);
	}
	if ((shouldRenderNext && shouldRenderPrevious) || (shouldRenderPrevious && shouldRenderSubmit)) {
		elements.push(NBSP);
	}
	if (shouldRenderNext) {
		elements.push(
			<Button
				style={
					primaryColour
						? {
								backgroundColor: primaryColour,
								color: getContrastColor(primaryColour)
							}
						: {}
				}
				disabled={!allowNext}
				variant="secondary"
				color="primary"
				key="form-action-next"
				onClick={async e => await goToNextPage?.()}
				size="small"
			>
				Next
			</Button>
		);
	}

	if (shouldRenderSubmit) {
		elements.push(
			<div key="custom-submit-btn" style={{ cursor: allowNext ? 'pointer' : 'not-allowed' }}>
				<div style={{ pointerEvents: allowNext ? 'auto' : 'none', opacity: allowNext ? 1 : 0.5 }}>
					<Button
						size="small"
						variant="primary"
						style={
							primaryColour
								? {
										backgroundColor: primaryColour,
										color: getContrastColor(primaryColour)
									}
								: {}
						}
						disabled={submitButtonDisabled}
						onClick={async () => {
							try {
								await submitForm?.(getAnswers());
							} catch (e) {
								console.error('Error during survey submission:', e);
							}
						}}
					>
						{submitButtonLabel ?? 'Submit'}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			{required ? (
				<Inset>
					<Typography variant="subtitle2" style={{ fontWeight: '500' }}>
						Fields marked <RequiredAsterisk gapSize={'0'} /> are required
					</Typography>
				</Inset>
			) : (
				<></>
			)}
			<div style={{ display: 'flex', float: 'right', ...(formRenderMode === FormRenderMode.preview ? { padding: 15 } : { paddingTop: 15 }) }}>
				{elements}
			</div>
		</>
	);
};
